import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { isEqual } from 'lodash-es'
import { useUserStore } from '@/stores/userStore'

export const useGenerationPreferencesStore = defineStore('generationPreferences', () => {
  // Initialize with all known keys to ensure Vue tracks them
  const generationPreferences = reactive<Record<string, string>>({
    TASK_TYPE: 'FORMAT',
    WRITING_TASK: '',
    FORMAT: '',
    STYLE: '',
    LANG: '',
    BRIEFING: ''
  })
  const initialGenerationPreferences = ref<Record<string, string>>({})

  const userStore = useUserStore()

  const propertiesMap = computed(
    () => userStore.user?.organisation?.organisationCustomization?.propertiesMap || {}
  )

  const hasGenerationPreferencesChanged = computed(() => {
    return !isEqual(generationPreferences, initialGenerationPreferences.value)
  })

  const format = computed(() => {
    return propertiesMap.value['FORMAT']
  })
  const style = computed(() => {
    return propertiesMap.value['STYLE']
  })
  const language = computed(() => {
    return propertiesMap.value['LANG']
  })

  /**
   * Returns the ComTask that has not been used for the recent generation
   */
  const unusedComTasks = computed(() => {
    const allComTasks = propertiesMap.value['FORMAT']
    return (
      allComTasks?.filter(c => {
        return (
          c.length > 0 &&
          c !== initialGenerationPreferences.value['FORMAT'] &&
          c !== generationPreferences['FORMAT']
        )
      }) || []
    )
  })

  function updateInitialGenerationPreferences() {
    initialGenerationPreferences.value = { ...generationPreferences }
    return initialGenerationPreferences.value
  }

  function updateFromPropertiesKeys(propertiesKeys: Record<string, string[]>) {
    if (propertiesKeys !== undefined) {
      Object.keys(propertiesKeys).forEach(key => {
        generationPreferences[key] = propertiesKeys[key][0]
      })
    }
  }

  function setDefaultForKey(key: string, map: Record<string, string[]>) {
    if (map[key]?.some(str => str.toLowerCase() === 'german' && key === 'LANG')) {
      generationPreferences[key] =
        map[key]?.find(str => str.toLowerCase() === 'german') || map[key][0]
    } else if (key === 'STYLE') {
      generationPreferences[key] =
        map[key].find(
          styleOption =>
            styleOption.toLowerCase() !== 'easy language' &&
            styleOption.toLowerCase() !== 'plain language'
        ) || map[key][0]
    } else if (key === 'BRIEFING') {
      generationPreferences[key] = ''
    } else if (map[key] && map[key][0]) {
      generationPreferences[key] = map[key][0]
    }
  }

  function reset() {
    // Reset to empty strings instead of deleting
    Object.keys(generationPreferences).forEach(key => {
      generationPreferences[key] = ''
    })
    // Reset special keys to defaults
    generationPreferences['TASK_TYPE'] = 'FORMAT'
    generationPreferences['WRITING_TASK'] = ''
    initialGenerationPreferences.value = {}

    // Re-apply defaults from propertiesMap after reset
    const map = propertiesMap.value
    if (Object.keys(map).length > 0) {
      Object.keys(map).forEach(key => {
        setDefaultForKey(key, map)
      })
    }
  }

  return {
    generationPreferences,
    format,
    style,
    language,
    initialGenerationPreferences,
    hasGenerationPreferencesChanged,
    unusedComTasks,
    updateInitialGenerationPreferences,
    updateFromPropertiesKeys,
    reset
  }
})
